// The /pure entry point has no import side effects — the default @stripe/stripe-js
// module starts injecting the Stripe.js script the moment it is imported, which
// would load it on every page that bundles this file.
import { loadStripe } from "@stripe/stripe-js/pure"

import type { Cart } from "../components/Cart"
import {
  type CheckoutMode,
  ProductType,
  PurchasePaymentMethod,
  UserInfoField,
} from "../generated/graphql"
import { isRecord } from "./json"

// ============================================================================
// TYPES
// ============================================================================

interface UserInfo {
  username?: string | null
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  birthday?: any | null
  avatar?: string | null
  emailVerified?: boolean | null
}

export interface DiscountAndFees {
  discount: number
  fees: number
  deliveryFees: number
  vat: number
  invalidProductIds?: string[]
  /** Held until approval (`JoinRequest`) or charged now (`Purchase`); resolved by the API. */
  checkoutMode?: CheckoutMode
  /** The community's methods narrowed for this cart and viewer; resolved by the API. */
  paymentMethods?: PurchasePaymentMethod[]
}

export interface PickupLocation {
  name: string
  address: {
    line1: string
    line2?: string
    postalCode?: string
    city?: string
    country?: string
  }
  hours: Array<{
    days: string
    hours: string
  }>
  phone?: string
  email?: string
  mapUrl?: string
}

export interface ShippingAddress {
  fullName: string
  streetAddress: string
  streetAddress2?: string
  postalCode: string
  city: string
  country: string
}

export type PrintfulShippingSelection = {
  currency: string
  rate: number
  shipping: string
  shippingMethodName?: string | null
}

interface CartAnalysis {
  hasTickets: boolean
  hasMerch: boolean
  pickupLocation?: PickupLocation
  hasPickupOption: boolean
  merchRequiresPickup: boolean
}

interface ProductVariant {
  size?: string
  color?: string
}

type MerchDeliveryConfig = {
  pickupLocation?: PickupLocation
  noShipping?: boolean | null
  pickupOnly?: boolean | null
}

type PrintfulVariant = {
  color?: string
  colorCode?: string
  size?: string
  variantId: number
}

type PrintfulProductConfig = {
  currency?: string
  productTemplateId?: number
  variants?: PrintfulVariant[]
}

// ============================================================================
// CONSTANTS
// ============================================================================

const REQUIRED_SHIPPING_FIELDS: (keyof ShippingAddress)[] = [
  "fullName",
  "streetAddress",
  "city",
  "postalCode",
  "country",
]

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

type StripeWalletPaymentMethod = "StripeApplePay" | "StripeGooglePay"
export type CheckoutPaymentMethod = PurchasePaymentMethod | StripeWalletPaymentMethod

/**
 * Converts frontend-only wallet payment methods to backend-compatible format.
 * Backend doesn't distinguish Stripe wallet payments from StripeCard.
 */
export const toBackendPaymentMethod = (
  paymentMethod: CheckoutPaymentMethod | string,
): PurchasePaymentMethod => {
  if (paymentMethod === "StripeApplePay" || paymentMethod === "StripeGooglePay") {
    return PurchasePaymentMethod.StripeCard
  }
  return paymentMethod as PurchasePaymentMethod
}

export const isUserInfoComplete = (
  user?: UserInfo | null,
  requiredFields: UserInfoField[] = [],
): boolean => {
  if (!user) return false

  // Checkout base requirement, regardless of community config.
  // emailVerified is checked separately at checkout time, not here.
  if (!user.username || !user.email) {
    return false
  }

  const required = new Set(requiredFields)

  if (required.has(UserInfoField.FirstName) && (user.firstName?.trim().length ?? 0) < 2)
    return false
  if (required.has(UserInfoField.LastName) && (user.lastName?.trim().length ?? 0) < 2) return false
  if (required.has(UserInfoField.Dob) && !user.birthday) return false

  return true
}

export const isShippingAddressValid = (address: Record<string, string>): boolean => {
  return REQUIRED_SHIPPING_FIELDS.every((field) => {
    const value = address[field]
    return value && value.trim().length > 0
  })
}

const getMerchDeliveryConfig = (additionalConfig: unknown): MerchDeliveryConfig | undefined => {
  if (!isRecord(additionalConfig)) {
    return undefined
  }

  return additionalConfig as MerchDeliveryConfig
}

export const getPickupLocation = (additionalConfig: unknown): PickupLocation | undefined => {
  return getMerchDeliveryConfig(additionalConfig)?.pickupLocation
}

export const isMerchPickupOnly = (additionalConfig: unknown): boolean => {
  const config = getMerchDeliveryConfig(additionalConfig)
  return config?.noShipping === true || config?.pickupOnly === true
}

export const getPrintfulProductConfig = (
  additionalConfig: unknown,
): PrintfulProductConfig | null => {
  if (!isRecord(additionalConfig) || !isRecord(additionalConfig.printfulEwl)) {
    return null
  }

  const printful = additionalConfig.printfulEwl
  return printful.productTemplateId ? (printful as PrintfulProductConfig) : null
}

export const isPrintfulProduct = (additionalConfig: unknown) =>
  Boolean(getPrintfulProductConfig(additionalConfig))

export const analyzeCart = (cart: Cart, code: string): CartAnalysis => {
  if (!cart[code]) {
    return {
      hasMerch: false,
      hasPickupOption: false,
      hasTickets: false,
      merchRequiresPickup: false,
      pickupLocation: undefined,
    }
  }

  const cartItems = Object.values(cart[code])
  const merchItems = cartItems.filter((item) => item.product.productType === ProductType.Merch)

  const hasTickets = cartItems.some((item) => item.product.productType === ProductType.Ticket)

  const hasMerch = merchItems.length > 0
  const merchRequiresPickup = merchItems.some((item) =>
    isMerchPickupOnly(item.product.additionalConfig),
  )

  // Extract pickup location from the first pickup-only merch product, or fall back to the first merch item.
  let pickupLocation: PickupLocation | undefined
  if (hasMerch) {
    const pickupLocationSource =
      merchItems.find((item) => isMerchPickupOnly(item.product.additionalConfig)) ?? merchItems[0]

    if (pickupLocationSource) {
      pickupLocation = getPickupLocation(pickupLocationSource.product.additionalConfig)
    }
  }

  return {
    hasMerch,
    hasPickupOption: Boolean(pickupLocation),
    hasTickets,
    merchRequiresPickup,
    pickupLocation,
  }
}

/**
 * Formats a product variant as a unique key for cart operations
 * @example formatVariantKey({ size: 'M', color: 'Black' }) => "M-Black"
 * @example formatVariantKey({ size: 'L' }) => "L-none"
 * @example formatVariantKey(undefined) => "none-none"
 */
export const formatVariantKey = (variant?: ProductVariant): string => {
  return `${variant?.size || "none"}-${variant?.color || "none"}`
}

/**
 * Formats a product variant for human-readable display
 * @example formatVariantDisplay({ size: 'M', color: 'Black' }) => "Size M, Black"
 * @example formatVariantDisplay({ size: 'L' }) => "Size L"
 * @example formatVariantDisplay({ color: 'Red' }) => "Red"
 * @example formatVariantDisplay(undefined) => ""
 */
export const formatVariantDisplay = (variant?: ProductVariant): string => {
  if (!variant?.size && !variant?.color) return ""

  const parts: string[] = []
  if (variant.size) parts.push(`Size ${variant.size}`)
  if (variant.color) parts.push(variant.color)

  return parts.join(", ")
}

/**
 * Formats a product variant for compact display with bullet separator
 * @example formatVariantDisplayCompact({ size: 'M', color: 'Black' }) => "SIZE: M • Black"
 * @example formatVariantDisplayCompact({ size: 'L' }) => "SIZE: L"
 * @example formatVariantDisplayCompact(undefined) => ""
 */
const _formatVariantDisplayCompact = (variant?: ProductVariant): string => {
  if (!variant?.size && !variant?.color) return ""

  const parts: string[] = []
  if (variant.size) parts.push(`SIZE: ${variant.size}`)
  if (variant.color) parts.push(variant.color)

  return parts.join(" • ")
}

// ============================================================================
// STRIPE WARM-UP
// ============================================================================

let stripeWarmupStarted = false

/**
 * Kicks off the js.stripe.com/v3 script download ahead of checkout. loadStripe
 * caches the script load, so the checkout page's own loadStripe call (with the
 * community's connected account) resolves instantly once this has run.
 *
 * Client-only; the guard returns before the module flag is touched during SSR,
 * so no state leaks across server invocations.
 */
export const warmupStripeJs = () => {
  if (stripeWarmupStarted || typeof window === "undefined") return

  const stripePK = process.env.NEXT_PUBLIC_STRIPE_PK
  if (!stripePK) return

  stripeWarmupStarted = true
  void loadStripe(stripePK, { locale: "en" }).catch(() => {
    // Warm-up is best-effort; checkout retries with its own loadStripe call.
  })
}
